import type { ParcelInfo } from "../types/deforestation";

const TRANSITION_COLORS = {
  "tree loss": "#ef4444",
  "density reduction": "#f59e0b",
  "tree gain": "#22c55e",
  stable: "#a1a1aa",
} as const;

type TransitionType = keyof typeof TRANSITION_COLORS;

interface TransitionDonutProps {
  parcels: ParcelInfo[];
}

export function TransitionDonut({ parcels }: TransitionDonutProps) {
  const totals: Record<TransitionType, number> = {
    "tree loss": 0,
    "density reduction": 0,
    "tree gain": 0,
    stable: 0,
  };
  for (const p of parcels) {
    const c = p.stats?.forestChange;
    if (!c) continue;
    totals["tree loss"] += c.tree_loss_m2;
    totals["density reduction"] += c.density_reduction_m2;
    totals["tree gain"] += c.tree_gain_m2;
    totals["stable"] += c.stable_m2;
  }
  const totalM2 =
    totals["tree loss"] +
    totals["density reduction"] +
    totals["tree gain"] +
    totals.stable;
  const totalHa = totalM2 / 10_000;

  const order: { type: TransitionType; label: string }[] = [
    { type: "tree loss", label: "Tree Loss" },
    { type: "density reduction", label: "Density Reduction" },
    { type: "tree gain", label: "Tree Gain" },
    { type: "stable", label: "Stable" },
  ];
  const rows = order.map(({ type, label }) => {
    const m2 = totals[type];
    return {
      type,
      label,
      ha: m2 / 10_000,
      pct: totalM2 > 0 ? (m2 / totalM2) * 100 : 0,
    };
  });

  let cumulative = 0;
  const stops: string[] = [];
  for (const r of rows) {
    if (r.pct === 0) continue;
    const start = cumulative;
    cumulative += r.pct;
    stops.push(
      `${TRANSITION_COLORS[r.type]} ${start.toFixed(1)}% ${cumulative.toFixed(1)}%`,
    );
  }
  const gradient =
    stops.length > 0
      ? `conic-gradient(${stops.join(", ")})`
      : `conic-gradient(${TRANSITION_COLORS.stable} 0% 100%)`;

  return (
    <div className="rounded-lg border bg-muted/30 p-4">
      <h3 className="mb-3 text-sm font-medium text-foreground">
        Group — Transitions by Area
      </h3>
      <div className="flex items-center gap-5">
        <div className="relative shrink-0">
          <div
            className="h-20 w-20 rounded-full"
            style={{ background: gradient }}
          >
            <div className="flex h-full w-full items-center justify-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-background text-xs font-semibold text-foreground">
                {totalHa.toLocaleString(undefined, { maximumFractionDigits: 0 })}{" "}
                Ha
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-1.5 text-xs">
          {rows.map((r) => (
            <div key={r.type} className="flex items-center gap-2">
              <span
                className="h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ backgroundColor: TRANSITION_COLORS[r.type] }}
              />
              <span className="text-muted-foreground">{r.label}:</span>
              <span className="text-muted-foreground">
                {r.ha.toLocaleString(undefined, { maximumFractionDigits: 1 })} Ha
                ({r.pct.toFixed(0)}%)
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
